import { Injectable, inject } from '@angular/core';
import { SessionStorage, SessionStorageService } from '@mwe/utils';
import { Abschlagsplan, AddressProducts, ILSystem, IPhysischePerson, createAddress } from '@mwe/models';

const abschlagsplanKey = 'partialPayment_abschlagsplan';
const addressProductsKey = 'partialPayment_addressProducts';
const systemIdKey = 'partialPayment_systemId';
const accountIdKey = 'partialPayment_accountId';
const hasOnlyOneSelectionItemKey = 'partialPayment_hasOnlyOneSelectionItem';
const newAmountKey = 'partialPayment_newAmount';
const wantedAmountKey = 'partialPayment_wantedAmount';
const newStartDateKey = 'partialPayment_newStartDate';
const oldAmountKey = 'partialPayment_oldAmount';
const lastOrderIdKey = 'partialPayment_lastOrderId';
const retriedOnErrorKey = 'partialPayment_retriedOnError';
const submitIdKey = 'partialPayment_submitId';
const physischePersonKey = 'partialPayment_physischePerson';
const numberOfOpenChangesKey = 'partialPayment_numberOfOpenChanges';

@Injectable({ providedIn: 'root' })
export class PartialPaymentStateService {
  private storage = inject(SessionStorageService);

  keepSessionStorageOnce = false;

  @SessionStorage(abschlagsplanKey)
  private _abschlagsplan: Abschlagsplan;

  get abschlagsplan(): Abschlagsplan {
    return this._abschlagsplan;
  }

  set abschlagsplan(value: Abschlagsplan) {
    if (!value) {
      this.storage.clear(abschlagsplanKey);
    } else {
      this._abschlagsplan = value;
    }
  }

  @SessionStorage(addressProductsKey)
  private _addressProducts: AddressProducts;

  get addressProducts(): AddressProducts {
    if (!this._addressProducts) {
      return undefined;
    }

    this._addressProducts.address = createAddress(this._addressProducts.address);
    return this._addressProducts;
  }

  set addressProducts(addressProducts: AddressProducts) {
    if (!addressProducts) {
      this.storage.clear(addressProductsKey);
    } else {
      this._addressProducts = addressProducts;
    }
  }

  @SessionStorage(accountIdKey)
  private _accountId: string;

  get accountId(): string {
    return this._accountId;
  }

  set accountId(accountId: string) {
    this._accountId = accountId;
  }

  @SessionStorage(systemIdKey)
  private _systemId: ILSystem;

  get systemId(): ILSystem {
    return this._systemId;
  }

  set systemId(systemId: ILSystem) {
    this._systemId = systemId;
  }

  @SessionStorage(hasOnlyOneSelectionItemKey)
  private _hasOnlyOneSelectionItem: boolean;

  get hasOnlyOneSelectionItem(): boolean {
    return this._hasOnlyOneSelectionItem;
  }

  set hasOnlyOneSelectionItem(noNeedForBackButton: boolean) {
    this._hasOnlyOneSelectionItem = noNeedForBackButton;
  }

  @SessionStorage(newAmountKey)
  private _newAmount: number;

  get newAmount(): number {
    return this._newAmount;
  }

  // new amount calculated from IL
  set newAmount(newAmount: number) {
    this._newAmount = newAmount;
  }

  @SessionStorage(wantedAmountKey)
  private _wantedAmount: number;

  get wantedAmount(): number {
    return this._wantedAmount;
  }

  // user input
  set wantedAmount(wantedAmount: number) {
    this._wantedAmount = wantedAmount;
  }

  @SessionStorage(oldAmountKey)
  private _oldAmount: number;

  get oldAmount(): number {
    return this._oldAmount;
  }

  set oldAmount(oldAmount: number) {
    this._oldAmount = oldAmount;
  }

  @SessionStorage(newStartDateKey)
  private _newStartDate: string;

  get newStartDate(): string {
    return this._newStartDate;
  }

  set newStartDate(startDate: string) {
    this._newStartDate = startDate;
  }

  @SessionStorage(lastOrderIdKey)
  private _lastOrderId: string;

  get lastOrderId(): string {
    return this._lastOrderId || undefined;
  }

  set lastOrderId(lastOrderId: string) {
    if (lastOrderId) {
      this._lastOrderId = lastOrderId;
    } else {
      this.storage.clear(lastOrderIdKey);
    }
  }

  @SessionStorage(retriedOnErrorKey)
  private _retriedOnError: boolean;

  get retriedOnError(): boolean {
    return this._retriedOnError || undefined;
  }

  set retriedOnError(retriedOnError: boolean) {
    if (retriedOnError) {
      this._retriedOnError = retriedOnError;
    } else {
      this.storage.clear(retriedOnErrorKey);
    }
  }

  @SessionStorage(submitIdKey)
  private _submitId: number;

  get submitId(): number {
    return this._submitId || undefined;
  }

  set submitId(submitId: number) {
    if (submitId) {
      this._submitId = submitId;
    } else {
      this.storage.clear(submitIdKey);
    }
  }

  @SessionStorage(physischePersonKey)
  private _physischePerson: IPhysischePerson;

  get physischePerson(): IPhysischePerson {
    return this._physischePerson || undefined;
  }

  set physischePerson(physischePerson: IPhysischePerson) {
    if (physischePerson) {
      this._physischePerson = physischePerson;
    } else {
      this.storage.clear(physischePersonKey);
    }
  }

  @SessionStorage(numberOfOpenChangesKey)
  private _numberOfOpenChanges: number;

  get numberOfOpenChanges(): number {
    return this._numberOfOpenChanges || undefined;
  }

  set numberOfOpenChanges(numberOfOpenChanges: number) {
    if (numberOfOpenChanges >= 0) {
      this._numberOfOpenChanges = numberOfOpenChanges;
    } else {
      this.storage.clear(numberOfOpenChangesKey);
    }
  }

  clear(): void {
    this.storage.clear(retriedOnErrorKey);
    this.storage.clear(submitIdKey);
    this.storage.clear(newAmountKey);
    this.storage.clear(wantedAmountKey);
    this.storage.clear(oldAmountKey);
    this.storage.clear(newStartDateKey);

    if (this.keepSessionStorageOnce) {
      this.keepSessionStorageOnce = false;
      return;
    }

    this.storage.clear(lastOrderIdKey);
    this.storage.clear(abschlagsplanKey);
    this.storage.clear(physischePersonKey);
    this.storage.clear(addressProductsKey);
    this.storage.clear(accountIdKey);
    this.storage.clear(systemIdKey);
    this.storage.clear(hasOnlyOneSelectionItemKey);
  }
}
